import { t } from "i18next";
import isEmpty from "lodash/isEmpty";
import round from "lodash/round";
import sortBy from "lodash/sortBy";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useAppMode } from "src/contexts/app-mode";

import CustomToastify from "@components/shared/CustomToastify/CustomToastify";
import {
  BOOKING_ADVANCE_DURATION,
  BOOKING_CONSTANTS,
  PAYMENT_OPTIONS,
  PAYMENT_STATUS,
  QUOTA_INFO_ARRAY,
} from "@constants/Bookings";
import { CONSTANTS } from "@constants/index";
import { useLocationContext } from "@contexts/location/LocationContext";
import { conditionalStyle, slotType, vatHandlingType } from "@enums/hooks.enum";
import { supportEnum } from "@enums/route.enum";
import { DateFormats } from "@enums/shared.enum";
import { usePaymentMethod } from "@hooks/usePaymentMethod/usePaymentMethod";
import {
  POSTHOG_ENABLED,
  trackChargeSucceeded,
  useTrackBookingConfirmed,
} from "@hooks/usePosthog/usePosthog";
import { SavedCardModalStatus } from "@models/components/paymentMethod.model";
import {
  AvailableTiming,
  BookingFetchResponseType,
  BookingSaveApi,
  BookingSlotApi,
  QuotaMessage,
  ResourceType,
  SingleSlot,
  timeMapObj,
} from "@models/services/booking";
import handleResponse from "@services/apiConfig/api.response.service";
import { applyLocalization } from "@services/common.service";
import API_SERVICES from "@services/index";
import bookingSlice from "@slices/booking.slice";
import kioskSlice from "@slices/kiosk.slice";
import modalSlice from "@slices/modal.slice";
import { RootState } from "@store/index";
import {
  normalizeSlotEnd,
  policyLateBookingWindowMinutes,
} from "@utils/booking.utils";
import commonUtils from "@utils/common.utils";

import { useBackToBackBooking } from "../useBackToBackBooking/useBackToBackBooking";

export const useBooking = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isKioskMode } = useAppMode();
  const {
    trackBookingConfirmed,
    trackBookingEdited,
    trackBookingEditSubmitted,
    trackSlotSelected,
  } = useTrackBookingConfirmed();

  const { setTimeSlotSelectionBackToBack } = useBackToBackBooking();

  const {
    backToBackBookingEnabled,
    backToBackBookingSlots,
    bookingEditData,
    isEditBooking,
    resourcePrices,
    season,
    selectedDate,
    selectedSlot,
    selectedTime,
  } = useSelector((State: RootState) => State.booking);

  const { savedMethods, scheduledTime } = usePaymentMethod({ isBooking: true });

  const { activeLocation, locationDetails, paymentRef, pricingRef } =
    useLocationContext();
  const scrollFocusToPayment = () => {
    if (!paymentRef?.current && !pricingRef?.current) return;

    const target = paymentRef?.current || pricingRef?.current;
    target?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };
  const postToPaymentUrl = (
    url: string,
    bookingId?: string,
    grandTotalAmount?: number | string,
    extras?: string,
    paymentUuid?: string
  ) => {
    const clubName =
      (locationDetails?.title
        ? applyLocalization(locationDetails.title)
        : "") ||
      (activeLocation?.info?.title
        ? applyLocalization(activeLocation.info.title)
        : "");

    const locationName = activeLocation?.info?.title
      ? applyLocalization(activeLocation.info.title)
      : "";

    const clubLogo =
      locationDetails?.clubLogo ||
      locationDetails?.mobileLogo ||
      locationDetails?.fallbackLogo;

    const resource = activeLocation?.resources?.find(
      r => r.id === selectedSlot?.resourceId
    );
    const facilityName =
      (resource?.title ? applyLocalization(resource.title) : "") || "";
    const facilityCategory = activeLocation?.resourceType?.terminology?.singular
      ? applyLocalization(activeLocation.resourceType.terminology.singular)
      : "";

    const dateStr = selectedDate
      ? moment(selectedDate).format("DD.MM.YYYY")
      : "";
    const slotDuration = selectedSlot?.slot?.slot || 30;
    const startTimeStr = selectedTime || "08:00";
    const endTimeStr =
      selectedTime && slotDuration
        ? moment(selectedTime, "HH:mm")
            .add(slotDuration, "minutes")
            .format("HH:mm")
        : "";
    const timeStr =
      startTimeStr && endTimeStr
        ? `${startTimeStr}–${endTimeStr}`
        : startTimeStr;
    const durationStr = `(${slotDuration} min)`;

    const resourceId = selectedSlot?.resourceId;
    const durationMinutes = selectedSlot?.slot?.slot;
    const durationKey = durationMinutes ? `T_${durationMinutes}` : "";
    const priceInfo =
      resourceId && durationKey
        ? resourcePrices?.prices?.[resourceId]?.durations?.[durationKey]
        : null;

    const rawTotalAmount =
      grandTotalAmount !== undefined && grandTotalAmount !== null
        ? grandTotalAmount
        : (priceInfo?.totalPriceWithTax ?? priceInfo?.totalPrice ?? 0);

    let totalAmount = rawTotalAmount;
    if (
      isEditBooking &&
      bookingEditData?.totalPrice !== undefined &&
      bookingEditData?.totalPrice !== null
    ) {
      const priceDiff =
        Math.round(
          (Number(rawTotalAmount) - Number(bookingEditData.totalPrice)) * 100
        ) / 100;
      if (priceDiff > 0) {
        totalAmount = priceDiff;
      }
    }

    const currency = resourcePrices?.currency || "CHF";
    const vatObj = resourcePrices?.vat;
    const hasVat =
      Boolean(vatObj?.hasVat) && Number(vatObj?.vatPercentage || 0) > 0;
    const vatRate = vatObj?.vatPercentage ? `${vatObj.vatPercentage}%` : "";
    const isVatInclusive =
      vatObj?.vatHandlingType !== vatHandlingType.EXCLUSIVE;

    const selectedResourceId = selectedSlot?.resourceId;
    const currentResourcePrice = selectedResourceId
      ? resourcePrices?.prices?.[selectedResourceId]
      : undefined;

    const isTokenizationAllowed =
      currentResourcePrice &&
      typeof currentResourcePrice.allowTokenization === "boolean"
        ? currentResourcePrice.allowTokenization
        : true;

    dispatch(bookingSlice.actions.setPaymentLoader(false));
    dispatch(
      modalSlice.actions.updatePaymentSummary({
        allowTokenization: isTokenizationAllowed,
        amountToPaid: rawTotalAmount,
        bookingId,
        clubLogo,
        clubName,
        currency,
        dateStr,
        durationStr,
        extras,
        facilityCategory,
        facilityName,
        hasVat,
        isVatInclusive,
        locationName,
        paymentUuid,
        timeStr,
        totalAmount,
        vatRate,
      })
    );
    dispatch(modalSlice.actions.updatePaymentIframeUrl(url));
    dispatch(modalSlice.actions.updatePaymentIframeModal(true));
  };

  const showSavedCardConfirmedModal = (
    payload: BookingSaveApi,
    modalStatus: SavedCardModalStatus = "success",
    referenceId?: string,
    waitForTokenization?: boolean,
    responseData?: Record<string, unknown>
  ) => {
    const resource = activeLocation?.resources?.find(
      r => r.id === selectedSlot?.resourceId
    );
    const facilityName = resource?.title
      ? applyLocalization(resource.title)
      : "";
    const facilityCategory = activeLocation?.resourceType?.terminology?.singular
      ? applyLocalization(activeLocation.resourceType.terminology.singular)
      : "";

    const slotDuration = selectedSlot?.slot?.slot;
    const startTimeStr = selectedTime || "";
    const endTimeStr =
      selectedTime && slotDuration
        ? moment(selectedTime, DateFormats.Time)
            .add(slotDuration, DateFormats.Minutes)
            .format(DateFormats.Time)
        : "";
    const timeStr =
      startTimeStr && endTimeStr
        ? `${startTimeStr}–${endTimeStr}`
        : startTimeStr;

    const currency = resourcePrices?.currency || "";

    const dueAmountFromResponse =
      responseData?.dueAmount ?? responseData?.amount;

    const amountDiff =
      isEditBooking &&
      bookingEditData?.totalPrice !== undefined &&
      bookingEditData?.totalPrice !== null &&
      payload?.totalPrice !== undefined &&
      payload?.totalPrice !== null
        ? round(Number(payload.totalPrice), 2) -
          round(Number(bookingEditData.totalPrice), 2)
        : null;

    let actualPaidNumber: number;
    if (dueAmountFromResponse !== undefined && dueAmountFromResponse !== null) {
      actualPaidNumber = Number(dueAmountFromResponse);
    } else if (amountDiff !== null) {
      actualPaidNumber = amountDiff > 0 ? amountDiff : 0;
    } else if (
      payload?.totalPrice !== undefined &&
      payload?.totalPrice !== null
    ) {
      actualPaidNumber = Number(payload.totalPrice);
    } else {
      actualPaidNumber = 0;
    }

    const displayAmount =
      payload?.totalPrice !== undefined && payload?.totalPrice !== null
        ? actualPaidNumber.toFixed(2)
        : "";

    let cardBrand = "";
    let lastFour = "";
    const selectedMethod = savedMethods?.find(
      m => String(m.id) === String(payload?.tokenizationId)
    );
    if (selectedMethod) {
      cardBrand = selectedMethod.brand || "";
      lastFour = selectedMethod.lastFour || "";
    }

    const targetDate = payload?.date || selectedDate;
    const scheduleMoment = scheduledTime
      ? moment(scheduledTime, [
          CONSTANTS.DATE_FORMATS.YEAR_MONTH_DAY_TIME_SECONDS_FORMAT,
          CONSTANTS.DATE_FORMATS.YEAR_MONTH_DAY_TIME_FORMAT,
        ])
      : moment.invalid();

    const endMoment = selectedSlot?.slot?.ends
      ? moment(
          `${targetDate} ${selectedSlot.slot.ends}`,
          `${DateFormats.YMD} ${DateFormats.Time}`
        )
      : slotDuration && startTimeStr
        ? moment(
            `${targetDate} ${startTimeStr}`,
            `${DateFormats.YMD} ${DateFormats.Time}`
          ).add(slotDuration, DateFormats.Minutes)
        : moment.invalid();

    let scheduledDateStr = "";
    let scheduledTimeStr = "";
    if (scheduleMoment.isValid()) {
      scheduledDateStr = scheduleMoment.format(DateFormats.DayDateYear);
      scheduledTimeStr = scheduleMoment.format(DateFormats.Time);
    } else if (endMoment.isValid()) {
      scheduledDateStr = endMoment.format(DateFormats.DayDateYear);
      scheduledTimeStr = endMoment.format(DateFormats.Time);
    } else {
      scheduledDateStr = targetDate
        ? moment(targetDate, DateFormats.YMD).format(DateFormats.DayDateYear)
        : "";
      scheduledTimeStr = endTimeStr;
    }

    const clubName =
      (locationDetails?.title
        ? applyLocalization(locationDetails.title)
        : "") ||
      (activeLocation?.info?.title
        ? applyLocalization(activeLocation.info.title)
        : "");

    const formattedDateWithDay = targetDate
      ? moment(targetDate, DateFormats.YMD).format("dd DD.MM.YYYY")
      : "";
    const dateTimeStr =
      formattedDateWithDay && timeStr
        ? `${formattedDateWithDay}, ${timeStr}`
        : formattedDateWithDay || timeStr;

    const isEditOnBooking =
      Boolean(isEditBooking) && bookingEditData?.scheduledFrom === "ON_BOOKING";

    const amountToPaid =
      isEditBooking &&
      bookingEditData?.totalPrice !== undefined &&
      bookingEditData?.totalPrice !== null &&
      payload?.totalPrice !== undefined &&
      payload?.totalPrice !== null
        ? round(Number(payload.totalPrice), 2) -
          round(Number(bookingEditData.totalPrice), 2)
        : 0;

    const isEditOnBookingPaid = isEditOnBooking && amountToPaid <= 0;

    const paymentStatus =
      modalStatus === "processing"
        ? undefined
        : isEditOnBookingPaid
          ? PAYMENT_STATUS.SUCCESS
          : scheduleMoment.isValid()
            ? PAYMENT_STATUS.SCHEDULED
            : PAYMENT_STATUS.SUCCESS;

    dispatch(bookingSlice.actions.setPaymentLoader(false));
    dispatch(
      modalSlice.actions.updateSavedCardConfirmedSummary({
        amountToPaid,
        bookingDate: payload?.date,
        bookingId: (responseData?.bookingId ||
          responseData?.referenceId ||
          payload?.bookingId ||
          "") as string | number,
        cardBrand,
        clubName,
        currency,
        dateTimeStr,
        displayAmount,
        facilityCategory,
        facilityName,
        isEditOnBookingPaid,
        lastFour,
        modalStatus,
        paymentStatus,
        referenceId,
        scheduledDateStr,
        scheduledTimeStr,
        timeStr,
        waitForTokenization,
      })
    );
    dispatch(modalSlice.actions.updateSavedCardConfirmedModal(true));
  };

  const bookSlot = async (payload: BookingSaveApi) => {
    if (isEditBooking) {
      trackBookingEditSubmitted(payload, season);
    }
    dispatch(bookingSlice.actions.setPaymentLoader(true));
    const bookingApi = isEditBooking
      ? API_SERVICES.BOOKING.EDIT_BOOKING(
          payload as BookingSaveApi & { bookingId: string }
        )
      : API_SERVICES.BOOKING.SAVE_BOOKING(payload);
    await bookingApi.then(async response => {
      const responseData = handleResponse(response);

      if (responseData?.ok) {
        if (
          responseData?.data?.paymentUrl &&
          payload?.paymentMethod === PAYMENT_OPTIONS.INSTANT_PAYMENT &&
          (!payload?.tokenizationId || payload?.tokenizationId === "new")
        ) {
          const createdBookingId =
            responseData?.data?.bookingId ||
            responseData?.data?.referenceId ||
            responseData?.data?.id;

          const paymentUuid =
            responseData?.data?.paymentUuid ||
            responseData?.data?.paymentUUID ||
            responseData?.data?.uuid;

          const dueAmountFromResponse =
            responseData?.data?.dueAmount ?? responseData?.data?.amount;

          let paymentAmount =
            dueAmountFromResponse !== undefined &&
            dueAmountFromResponse !== null
              ? dueAmountFromResponse
              : payload?.totalPrice;

          if (
            dueAmountFromResponse === undefined &&
            isEditBooking &&
            bookingEditData?.totalPrice !== undefined &&
            bookingEditData?.totalPrice !== null
          ) {
            const priceDiff =
              Math.round(
                (Number(payload?.totalPrice) -
                  Number(bookingEditData.totalPrice)) *
                  100
              ) / 100;
            if (priceDiff > 0) {
              paymentAmount = priceDiff;
            }
          }

          postToPaymentUrl(
            responseData?.data?.paymentUrl,
            createdBookingId,
            paymentAmount,
            payload?.extras,
            paymentUuid
          );
        } else if (
          responseData?.data?.waitForTokenization &&
          responseData?.data?.referenceId
        ) {
          showSavedCardConfirmedModal(
            payload,
            "processing",
            responseData?.data?.referenceId,
            true,
            responseData?.data
          );
        } else if (
          payload?.paymentMethod === PAYMENT_OPTIONS.INSTANT_PAYMENT &&
          Boolean(payload?.tokenizationId) &&
          payload?.tokenizationId !== "new"
        ) {
          if (isEditBooking) {
            trackBookingEdited(responseData, payload, undefined, season);
          } else {
            trackBookingConfirmed(responseData, payload, undefined, season);
          }
          if (POSTHOG_ENABLED) {
            try {
              const createdBookingId =
                responseData?.data?.bookingId ||
                responseData?.data?.referenceId ||
                payload?.bookingId ||
                "";
              const dueAmount =
                responseData?.data?.dueAmount ?? responseData?.data?.amount;
              const chargeAmount =
                dueAmount !== undefined && dueAmount !== null
                  ? Number(dueAmount)
                  : Number(payload?.totalPrice) || 0;
              const selectedMethod = savedMethods?.find(
                m => String(m.id) === String(payload?.tokenizationId)
              );
              const methodTitle =
                selectedMethod?.brand || selectedMethod?.type || "Card";

              trackChargeSucceeded({
                amount: chargeAmount,
                booking_id: createdBookingId,
                payment_method: methodTitle,
              });
            } catch (error) {
              console.error("PostHog charge_succeeded tracking error:", error);
            }
          }
          showSavedCardConfirmedModal(
            payload,
            "success",
            undefined,
            false,
            responseData?.data
          );
        } else {
          if (isKioskMode) {
            handleKioskBookingSuccess(responseData);
            return;
          }

          dispatch(bookingSlice.actions.setPaymentLoader(false));
          if (isEditBooking) {
            trackBookingEdited(responseData, payload, undefined, season);
          } else {
            trackBookingConfirmed(responseData, payload, undefined, season);
          }

          commonUtils?.toastServiceWithOptions(
            <CustomToastify
              content={
                isEditBooking
                  ? t("bookingEdit.bookingUpdated")
                  : t("bookingConfirmed")
              }
              type="success"
              onButtonClick={() => navigate("/")}
              buttonText={t("moreBooking")}
            />,
            {
              autoClose: 20000,
              className: "fg-custom-booking-confirmed-toast-container",
            }
          );

          navigate(`${supportEnum.Reservations}?date=${payload?.date}`);
          dispatch(bookingSlice.actions.flush(null));
        }
      } else dispatch(bookingSlice.actions.setPaymentLoader(false));
    });
  };

  const handleKioskBookingSuccess = <
    T extends { data: { referenceId: string } },
  >(
    responseData: T
  ) => {
    trackBookingConfirmed(responseData, undefined, undefined, season);
    dispatch(bookingSlice.actions.flush(null));
    commonUtils?.toastService(t("bookingConfirmed"));

    dispatch(kioskSlice.actions.setKioskBookingJustSaved(true));
    dispatch(
      kioskSlice.actions.setBookingSuccessDetails({
        bookingId: responseData?.data?.referenceId,
        showModal: true,
      })
    );
  };

  const buildTimeSlot = (slots: BookingFetchResponseType) => {
    if (isEmpty(slots) || !slots?.resources?.length) {
      dispatch(bookingSlice.actions.updateTimeMaps([]));
      return;
    }

    const startTimes = slots.resources
      .map(resource => {
        const slotStart = resource.slotInfo?.slotStart;
        if (!slotStart) return null;
        const slotEnd = normalizeSlotEnd(resource.slotInfo?.slotEnd);
        if (slotEnd && moment(slotEnd).isBefore(moment())) return null;

        const momentStartTime = moment(slotStart);
        const isToday = selectedDate === moment().format(DateFormats.YMD);
        const lateBookingWindow = isToday
          ? policyLateBookingWindowMinutes(resource.policyInfo)
          : 0;
        return momentStartTime.subtract(lateBookingWindow, "minutes");
      })
      .filter(
        (momentStartTime): momentStartTime is moment.Moment => !!momentStartTime
      );

    let startTime =
      startTimes.length > 0 ? moment.min(startTimes) : moment.invalid();

    const endTimes = slots.resources
      .map(resource => {
        const slotEnd = normalizeSlotEnd(resource.slotInfo?.slotEnd);
        if (slotEnd && moment(slotEnd).isBefore(moment())) return null;
        return slotEnd;
      })
      .filter((slotEnd): slotEnd is string => !!slotEnd)
      .map(slotEnd => moment(slotEnd));

    const endTime =
      endTimes.length > 0 ? moment.max(endTimes) : moment.invalid();

    const slotTime = slots?.slotSplit ?? BOOKING_CONSTANTS.WIDTH_PER_SLOT;

    const allowedTimeSlots = Array.from(
      new Set(
        slots.resources.flatMap(r => r.policyInfo?.bookingStartTimes || [])
      )
    );
    const availableSlots = Array.from(
      new Set(
        slots.resources.flatMap(r => r.slotInfo?.slots?.availableSlots || [])
      )
    );

    const occupiedSlotsArrays = slots.resources
      .filter(r => r.slotInfo?.slots?.availableSlots?.length)
      .map(r => r.slotInfo?.slots?.occupiedSlots || []);

    let occupiedSlots: string[] = [];

    if (occupiedSlotsArrays.length) {
      occupiedSlots = occupiedSlotsArrays.reduce((acc, curr) =>
        acc.filter(time => curr.includes(time))
      );
    }

    if (moment(selectedDate, DateFormats.YMD).isBefore(moment(), "day")) {
      startTime = moment.invalid();
    }

    if (startTime.isValid()) {
      const dayStart = moment(selectedDate).startOf("day");
      startTime = moment.max(startTime, dayStart);
    }

    if (
      selectedDate === moment().format(DateFormats.YMD) &&
      startTime.isValid()
    ) {
      let maxLateBookingWindow = 0;
      slots.resources.forEach(r => {
        const isToday = selectedDate === moment().format(DateFormats.YMD);
        const mins = isToday ? policyLateBookingWindowMinutes(r.policyInfo) : 0;
        if (mins > maxLateBookingWindow) {
          maxLateBookingWindow = mins;
        }
      });
      const referenceTime = moment().subtract(maxLateBookingWindow, "minutes");
      startTime = getNextTimeMark(startTime, slotTime, referenceTime);
    }

    if (!startTime.isValid() || availableSlots.length === 0) {
      dispatch(bookingSlice.actions.updateTimeMaps([]));
      return;
    }

    const dayEnd = moment(selectedDate).endOf("day");
    const effectiveEndTime = moment.min(endTime, dayEnd);

    const applicableTimeSlots = generateTimeIntervals(
      startTime,
      effectiveEndTime,
      slotTime
    );
    const allowedSlots: string[] = Array.from(
      new Set(
        slots.resources.flatMap(r =>
          Object.keys(r.slotInfo?.slots?.allowedDurations || {}).map(key =>
            key.replace("T_", "")
          )
        )
      )
    );

    const notAllowedSlots = applicableTimeSlots.filter(
      time => !allowedSlots.includes(time)
    );

    const updatedOccupiedSlots = [...occupiedSlots, ...notAllowedSlots];

    const slotData = {
      availableSlots,
      occupiedSlots: updatedOccupiedSlots,
      unavailableSlots: applicableTimeSlots.filter(
        time =>
          !availableSlots.includes(time) || updatedOccupiedSlots.includes(time)
      ),
    };

    const timeSlotOptions = backToBackBookingEnabled
      ? setTimeSlotSelectionBackToBack(
          applicableTimeSlots,
          slotData,
          allowedTimeSlots,
          slots.resources
        )
      : setTimeSlotSelection(
          applicableTimeSlots,
          slotData,
          allowedTimeSlots,
          slots.resources
        );
    dispatch(
      bookingSlice.actions.changeIsSingleTimeSlot(allowedTimeSlots.length === 1)
    );
    dispatch(bookingSlice.actions.updateTimeMaps(timeSlotOptions));
  };

  const setTimeSlotSelection = (
    applicableTimeSlots: string[],
    slotData: {
      availableSlots: string[];
      occupiedSlots: string[];
      unavailableSlots: string[];
    },
    allowedTimeSlots: string[],
    resources: ResourceType[]
  ): Array<timeMapObj[]> => {
    const groupedByHour: Record<string, timeMapObj[]> = {};

    applicableTimeSlots.forEach(time => {
      const [hourStr, minuteStr] = time.split(":");
      if (
        (allowedTimeSlots.includes(minuteStr) ||
          (minuteStr === "00" && allowedTimeSlots.includes("60"))) &&
        slotData.availableSlots.includes(time)
      ) {
        let allowCount = 0;

        for (const resource of resources) {
          const slotDateTime = moment(
            `${selectedDate}T${time}`,
            DateFormats.YMDTime
          );
          if (slotDateTime.isBefore(moment())) {
            const isToday = selectedDate === moment().format(DateFormats.YMD);
            const lateBookingWindow = isToday
              ? policyLateBookingWindowMinutes(resource?.policyInfo)
              : 0;
            const lateBookingLimit = slotDateTime
              .clone()
              .add(lateBookingWindow, "minutes");
            if (moment().isAfter(lateBookingLimit)) {
              continue;
            }
          }

          const allowed = resource.slotInfo?.slots?.allowedDurations;
          const key = `T_${time}`;

          const durations =
            allowed?.[key] && typeof allowed[key] === "object"
              ? Object.keys(allowed[key]).map(k =>
                  parseInt(k.replace("T_", ""), 10)
                )
              : [];

          if (durations.length === 0) continue;

          const minDuration = Math.min(...durations);
          const requiredSlots = minDuration / BOOKING_CONSTANTS.WIDTH_PER_SLOT;

          let hour = Number(hourStr);
          let minute = Number(minuteStr);
          let allSlotsFree = true;

          for (let i = 0; i < requiredSlots; i++) {
            const timeStr = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;

            if (
              !slotData.availableSlots.includes(timeStr) ||
              (slotData.occupiedSlots.includes(timeStr) &&
                !isWithinOriginalEditRange(resource.id, timeStr))
            ) {
              allSlotsFree = false;
              break;
            }

            minute += BOOKING_CONSTANTS.WIDTH_PER_SLOT;
            if (minute >= BOOKING_CONSTANTS.MINS_IN_HOUR) {
              hour += Math.floor(minute / BOOKING_CONSTANTS.MINS_IN_HOUR);
              minute %= BOOKING_CONSTANTS.MINS_IN_HOUR;
            }
          }

          if (allSlotsFree) {
            allowCount++;
          }
        }

        const slot: timeMapObj = { disabled: allowCount === 0, time };

        if (!groupedByHour[hourStr]) groupedByHour[hourStr] = [];
        groupedByHour[hourStr].push(slot);
      }
    });

    return Object.keys(groupedByHour)
      .sort((a, b) => Number(a) - Number(b))
      .map(hour => groupedByHour[hour]);
  };
  const isWithinOriginalEditRange = (resourceId: string, timeStr: string) => {
    if (!isEditBooking) return false;

    if (selectedDate !== bookingEditData?.startDate) return false;

    if (bookingEditData?.resource?.id !== resourceId) return false;

    const start = moment(bookingEditData.startTime, DateFormats.Time);
    const end = moment(bookingEditData.endTime, DateFormats.Time);
    const slotTime = moment(timeStr, DateFormats.Time);

    if (!start.isValid() || !end.isValid() || !slotTime.isValid()) {
      return false;
    }

    // [start, end)
    return slotTime.isSameOrAfter(start) && slotTime.isBefore(end);
  };

  const getNextTimeMark = (
    targetTime: moment.Moment,
    interval: number,
    referenceTime: moment.Moment = moment()
  ): moment.Moment => {
    const currentTime = referenceTime;

    if (targetTime.isBefore(currentTime, DateFormats.Minutes)) {
      targetTime
        .year(currentTime.year())
        .month(currentTime.month())
        .date(currentTime.date())
        .hour(currentTime.hour())
        .minute(currentTime.minute());
    }

    const remainder = targetTime.minute() % interval;
    if (remainder !== 0) {
      targetTime.add(interval - remainder, DateFormats.Minutes);
    }

    if (interval === BOOKING_CONSTANTS.INTERVAL_30_MINUTES) {
      targetTime.minute(
        targetTime.minute() >= BOOKING_CONSTANTS.ROUNDED_30_MINUTE_MARK
          ? BOOKING_CONSTANTS.ROUNDED_30_MINUTE_MARK
          : BOOKING_CONSTANTS.ROUNDED_0_MINUTE_MARK
      );
    } else if (
      interval === BOOKING_CONSTANTS.INTERVAL_15_MINUTES &&
      targetTime.minute() >= BOOKING_CONSTANTS.MINS_IN_HOUR
    ) {
      targetTime.minute(BOOKING_CONSTANTS.ROUNDED_0_MINUTE_MARK);
    }

    return targetTime;
  };

  const generateTimeIntervals = (
    startTime: moment.Moment,
    endTime: moment.Moment,
    interval: number
  ): string[] => {
    const times: string[] = [];
    const currentTime = moment(startTime);
    while (
      currentTime.isSameOrBefore(endTime) &&
      currentTime.isSame(startTime, "day")
    ) {
      times.push(currentTime.format(DateFormats.Time));
      currentTime.add(interval, DateFormats.Minutes);
    }

    if (
      times.length > 0 &&
      moment(times[times.length - 1], DateFormats.Time).isAfter(endTime)
    ) {
      times.pop();
    }

    return times;
  };

  const cancelBooking = () => {
    dispatch(bookingSlice.actions.flush(null));
  };

  const disableResource = (
    isBookingDisabled: boolean,
    slots: Array<AvailableTiming>
  ) => {
    if (slots?.length === 0 || isBookingDisabled) {
      return conditionalStyle.Disabled;
    } else {
      return conditionalStyle.None;
    }
  };

  const disableTimeMap = (selectedSlot: SingleSlot) => {
    return selectedSlot?.slot?.type === slotType?.ReservationSlots
      ? "disabled"
      : "";
  };

  const fetchSlots = async ({ bookingDate, locationId }: BookingSlotApi) => {
    const data = await API_SERVICES.BOOKING.GET_BOOKING_SLOT({
      bookingDate,
      locationId,
    })
      .then(response => {
        const responseData = handleResponse(response);
        if (responseData?.ok) {
          dispatch(
            bookingSlice.actions.setBackToBackBookingEnabled(
              responseData.data?.backToBackBookingEnabled ?? false
            )
          );
          if (POSTHOG_ENABLED) {
            const rawResources = Array.isArray(response.data)
              ? response.data
              : response.data?.resources;
            const apiSeason =
              (response.data as { season?: string })?.season ||
              rawResources?.find((r: { season?: string }) => r?.season)?.season;
            if (apiSeason) {
              dispatch(bookingSlice.actions.setSeason(apiSeason));
            }
          }
          return response.data;
        }
      })
      .catch(() => {});
    return data;
  };

  const getSelectedResource = (
    slots: BookingFetchResponseType,
    resourceId = ""
  ) => {
    if (resourceId == "") resourceId = selectedSlot?.resourceId ?? "";

    const selectedResource = slots?.resources?.filter(
      obj => obj.id === resourceId
    )[0];
    return selectedResource;
  };

  const resourceActiveClass = (
    slot: AvailableTiming,
    resourceId: string
  ): string => {
    return selectedSlot?.resourceId === resourceId &&
      selectedSlot.slot?.slot === slot.slot &&
      selectedSlot?.isBookable
      ? "active"
      : "";
  };

  const selectSlot = (slotDetail: AvailableTiming, resourceId: string) => {
    const isSameSlot =
      selectedSlot?.resourceId === resourceId &&
      selectedSlot?.slot?.slot === slotDetail.slot;

    if (isSameSlot) {
      dispatch(bookingSlice.actions.changeSelectedSlot({} as SingleSlot));
    } else {
      dispatch(
        bookingSlice.actions.changeSelectedSlot({
          isBookable: true,
          resourceId,
          slot: slotDetail,
        })
      );
      trackSlotSelected(resourceId, slotDetail?.slot);
      // Do not clear newly-added amenities when changing slot/resource.
      // We allow them to persist so the user can see them and remove if needed (conflict logic handles warnings).
      // const isSameResource = selectedSlot?.resourceId === resourceId;
      // if (!isSameResource) {
      //   dispatch(
      //     bookingSlice.actions.setSelectedAmenities(
      //       selectedAmenitie.filter(sa => sa.isBooked)
      //     )
      //   );
      // }
    }
    dispatch(bookingSlice.actions.setBookingDataChanged(true));
    scrollFocusToPayment();
  };

  const getAvailableDurations = (
    resourceDetails: ResourceType
  ): { durations: AvailableTiming[]; warningMessage: QuotaMessage } => {
    const finalSlots: AvailableTiming[] = [];

    const warningMessage: QuotaMessage = {
      advanceBookingMsg: "",
      quote: [],
    };

    if (resourceDetails?.quotaInfo) {
      QUOTA_INFO_ARRAY.forEach(quotaInfo => {
        if (resourceDetails?.quotaInfo?.[quotaInfo]) {
          warningMessage.quote.push(
            resourceDetails?.quotaInfo?.[quotaInfo]?.quotaMessage
          );
        }
      });
    }

    const availableSlots = resourceDetails.slotInfo.slots.availableSlots;
    const allowedDurations = resourceDetails.slotInfo.slots.allowedDurations;
    const occupiedSlots = resourceDetails.slotInfo.slots.occupiedSlots;
    const allowBringingGuest = resourceDetails.policyInfo.allowBringGuest;
    const minguestIgnoreAmenities =
      resourceDetails.policyInfo?.minguestIgnoreAmenities;
    const key = `T_${selectedTime}`;

    if (allowedDurations[key]) {
      Object.values(allowedDurations[key]).forEach(durationObj => {
        const splitValue =
          durationObj.duration / BOOKING_CONSTANTS.WIDTH_PER_SLOT - 1;
        let slotAvailable = true;

        let timeMoment = moment(selectedTime, DateFormats.Time);
        const baseDate = moment(timeMoment.format(DateFormats.YMD));

        for (let i = 0; i <= splitValue; i++) {
          const currentTime = timeMoment.format(DateFormats.Time);

          if (
            availableSlots.includes(currentTime) &&
            allowedDurations[`T_${currentTime}`] &&
            allowedDurations[`T_${currentTime}`][`T_${durationObj.duration}`] &&
            !(
              occupiedSlots.includes(currentTime) &&
              !isOriginalEditSlot(resourceDetails.id, currentTime)
            ) &&
            moment(baseDate).isSame(moment(timeMoment.format(DateFormats.YMD)))
          ) {
            slotAvailable = true;
          } else {
            slotAvailable = false;
            break;
          }
          timeMoment = timeMoment.add(
            BOOKING_CONSTANTS.WIDTH_PER_SLOT,
            DateFormats.Minutes
          );
        }

        let isAdvancedBookable = true;

        const bookingEndAt: moment.Moment = moment(
          resourceDetails?.policyInfo?.bookingEndAt,
          DateFormats.YMDTimeWithSeconds
        );

        const selectedDateTimeMoment = moment(
          selectedDate + "T" + selectedTime,
          DateFormats.YMDTime
        );

        if (slotAvailable) {
          const slotStart = resourceDetails.slotInfo?.slotStart;
          const slotEnd = normalizeSlotEnd(resourceDetails.slotInfo?.slotEnd);

          if (slotStart) {
            const startLimit = moment(slotStart);
            if (
              startLimit.isValid() &&
              selectedDateTimeMoment.isBefore(startLimit)
            ) {
              slotAvailable = false;
            }
          }

          if (slotAvailable && slotEnd) {
            const endLimit = moment(slotEnd);
            const bookingEndDateTime = selectedDateTimeMoment
              .clone()
              .add(durationObj.duration, "minutes");
            if (endLimit.isValid() && bookingEndDateTime.isAfter(endLimit)) {
              slotAvailable = false;
            }
          }

          if (slotAvailable && selectedDateTimeMoment.isBefore(moment())) {
            const isToday = selectedDate === moment().format(DateFormats.YMD);
            const lateBookingWindow = isToday
              ? policyLateBookingWindowMinutes(resourceDetails.policyInfo)
              : 0;
            const lateBookingLimit = selectedDateTimeMoment
              .clone()
              .add(lateBookingWindow, "minutes");
            if (moment().isAfter(lateBookingLimit)) {
              slotAvailable = false;
            }
          }
        }

        if (
          bookingEndAt.isValid() &&
          selectedDateTimeMoment.isAfter(bookingEndAt) &&
          !isEmpty(resourceDetails?.policyInfo?.bookingAdvanceType) &&
          resourceDetails?.policyInfo?.bookingAdvanceValue
        ) {
          isAdvancedBookable = false;

          const message = t("Calendar.advanceBookingMsg", {
            time: subtractBookingAdvanceTime(
              selectedDateTimeMoment,
              resourceDetails?.policyInfo?.bookingAdvanceValue,
              resourceDetails?.policyInfo?.bookingAdvanceType
            ),
          });

          warningMessage.advanceBookingMsg = message;
        }

        if (slotAvailable) {
          const availableObj: AvailableTiming = {
            allowBringingGuest: allowBringingGuest,
            availableForBooking: slotAvailable,
            disable: !isAdvancedBookable || warningMessage.quote.length > 0,
            ends: timeMoment.format(DateFormats.Time),
            max: durationObj.max ?? null,
            min: durationObj.min ?? null,
            minguestIgnoreAmenities: minguestIgnoreAmenities,
            slot: durationObj.duration,
            starts: selectedTime,
            type: slotType.TimeSlots,
          };
          finalSlots.push(availableObj);
        }
      });
    }
    let availableFinalSlots = finalSlots;
    if (backToBackBookingEnabled) {
      const currentResourceSlots =
        backToBackBookingSlots?.find(
          slot => slot.resourceId === resourceDetails?.id
        )?.validAvailableSlots || [];
      availableFinalSlots = currentResourceSlots.includes(selectedTime)
        ? availableFinalSlots
        : [];
    }
    return { durations: sortBy(availableFinalSlots, "slot"), warningMessage };
  };

  const subtractBookingAdvanceTime = (
    momentDate: moment.Moment,
    amount: number,
    bookingAdvanceType: (typeof BOOKING_ADVANCE_DURATION)[keyof typeof BOOKING_ADVANCE_DURATION]
  ) => {
    switch (bookingAdvanceType) {
      case BOOKING_ADVANCE_DURATION.DAYS:
        momentDate = momentDate.subtract(amount, DateFormats.Days);
        break;
      case BOOKING_ADVANCE_DURATION.HOURS:
        momentDate = momentDate.subtract(amount, DateFormats.Hours);
        break;
      case BOOKING_ADVANCE_DURATION.MINUTES:
        momentDate = momentDate.subtract(amount, DateFormats.Minutes);
        break;
    }

    return momentDate.format(
      `${CONSTANTS.DATE_FORMATS.DAY_MONTH_YEAR_FORMAT}, ${CONSTANTS.DATE_FORMATS.HOUR_MINUTE_FORMAT}`
    );
  };

  const gotoToday = () => {
    dispatch(
      bookingSlice.actions.changeCalenderStartDate(
        moment().format(DateFormats.YMD)
      )
    );

    dispatch(
      bookingSlice.actions.changeSelectedDate(moment().format(DateFormats.YMD))
    );
  };

  const selectDate = (date: string) => {
    dispatch(
      bookingSlice.actions.changeSelectedDate(
        moment(date).format(DateFormats.YMD)
      )
    );
  };

  const selectFuture = (date: string) => {
    if (
      moment().format(DateFormats.YMD) === moment(date).format(DateFormats.YMD)
    ) {
      dispatch(
        bookingSlice.actions.changeCalenderStartDate(
          moment(date).format(DateFormats.YMD)
        )
      );
      selectDate(date);
    } else if (
      moment().diff(date, DateFormats.Days) >= DateFormats.Minus5Days
    ) {
      dispatch(
        bookingSlice.actions.changeCalenderStartDate(
          moment(date)
            .subtract(
              Math.abs(moment().diff(date, DateFormats.Days)) + 1,
              DateFormats.Days
            )
            .format(DateFormats.YMD)
        )
      );
      selectDate(date);
    } else {
      dispatch(
        bookingSlice.actions.changeCalenderStartDate(
          moment(date)
            .subtract(DateFormats.Add5Days, DateFormats.Days)
            .format(DateFormats.YMD)
        )
      );
      selectDate(date);
    }
  };

  const isOriginalEditSlot = (resourceId: string, time: string) => {
    if (!isEditBooking) return false;
    if (selectedDate !== bookingEditData?.startDate) return false;
    if (bookingEditData?.resource?.id !== resourceId) return false;
    const editStart = moment(bookingEditData.startTime, DateFormats.Time);
    const editEnd = moment(bookingEditData.endTime, DateFormats.Time);
    const slotTime = moment(time, DateFormats.Time);

    return slotTime.isSameOrAfter(editStart) && slotTime.isBefore(editEnd);
  };

  const isSlotAvailable = (duration: AvailableTiming[], slot: string) =>
    duration.find(s => s.slot == slot);

  const isTimeAvailable = (slots: Array<Array<timeMapObj>>, time: string) => {
    return slots.flat().some(slot => slot.time === time && !slot.disabled);
  };

  const fetchAmenities = async ({
    bookingDate,
    bookingId,
    locationId,
    resourceId,
    slotTime,
    startTime,
  }: {
    bookingDate: string;
    bookingId?: string;
    locationId: string;
    resourceId?: string;
    slotTime?: number | string;
    startTime: string;
  }) => {
    try {
      const response = await API_SERVICES.BOOKING.GET_AMENITIES({
        bookingDate,
        bookingId,
        locationId,
        resourceId,
        slotTime,
        startTime,
      });
      const responseData = handleResponse(response);
      if (responseData?.ok) {
        return responseData.data;
      }
      return null;
    } catch (err) {
      console.error("Error fetching amenities:", err);
      return null;
    }
  };

  return {
    bookSlot,
    buildTimeSlot,
    cancelBooking,
    disableResource,
    disableTimeMap,
    fetchAmenities,
    fetchSlots,
    getAvailableDurations,
    getNextTimeMark,
    getSelectedResource,
    gotoToday,
    isSlotAvailable,
    isTimeAvailable,
    resourceActiveClass,
    resourcePrices,
    scrollFocusToPayment,
    season,
    selectFuture,
    selectSlot,
    subtractBookingAdvanceTime,
  };
};
